import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Input, Spin, Empty, List, Popconfirm, Tag, Tooltip } from 'antd';
import {
  APPLICATION_FORM_SERVICES,
  APPLICATION_FORM_PACKAGES,
  PARTNER_SERVICES,
} from '../../constants/EntityName';
import { toastError, toastSuccess } from '../../service/Toast';
import callApi from '../../Api/config';
import { InvertFillButton } from '../../components/InvertFillButton';
import '../../components/style.scss';

interface AssignPackageModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  partnerId?: string;
  partnerName?: string;
  initialData?: any; // To support editing
  onDelete?: (id: string) => void;
  onSubmitLocal?: (data: any) => void;
  isGlobalSelectionMode?: boolean;
  generatedId?: number | null;
}

const AssignPackageModal: React.FC<AssignPackageModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  partnerId,
  partnerName,
  initialData,
  onDelete,
  onSubmitLocal,
  isGlobalSelectionMode,
  generatedId,
}) => {
  const [form] = Form.useForm();
  const serviceId = Form.useWatch('serviceId', form);
  const packageId = Form.useWatch('packageId', form);
  const aliasName = Form.useWatch('name', form);
  const documitraFee = Form.useWatch('documitraFee', form);
  const customPrice = Form.useWatch('customPrice', form);

  // State for Wizard Mode (Global Creation)
  const [services, setServices] = useState<any[]>([]);
  const [packages, setPackages] = useState<any[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(false);

  // State for Selection Mode (Pick from existing Global services)
  const [partnerServices, setPartnerServices] = useState<any[]>([]);
  const [loadingPartnerServices, setLoadingPartnerServices] = useState(false);
  const [selectedGlobalServiceIds, setSelectedGlobalServiceIds] = useState<string[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<any>(null);

  useEffect(() => {
    if (visible) {
      if (initialData) {
        // Edit mode
        form.setFieldsValue({
          name: initialData.name,
          serviceId: initialData.package?.service?.id,
          packageId: initialData.packageId,
          documitraFee: initialData.documitraFee,
          customPrice: initialData.customPrice,
        });
        if (initialData.package?.service?.id) {
          fetchPackages(initialData.package.service.id);
        }
        if (initialData.package) {
          setSelectedPackage(initialData.package);
        }
        // If editing a global service (no partnerId), we need to fetch all services for the dropdown
        if (!initialData.partnerId) {
          fetchInitial();
        }
      } else if (partnerId || isGlobalSelectionMode) {
        // ... (rest of the block)
      } else {
        fetchInitial();
      }
    } else {
      form.resetFields();
      setPackages([]);
      setPartnerServices([]);
      setSelectedGlobalServiceIds([]);
      setSelectedPackage(null);
    }
  }, [visible, partnerId, initialData, isGlobalSelectionMode]);

  // Helper to try parsing JSON strings if necessary
  const parseValue = (val: any) => {
    if (typeof val === 'string') {
      try {
        return JSON.parse(val);
      } catch (e) {
        return val; // return as string if not valid JSON
      }
    }
    return val;
  };

  const getParameterTags = (pkg: any) => {
    const params: { label: string; values: string[] }[] = [];
    if (!pkg) return params;

    // Key fields to check
    const keysToCheck = [
      { key: 'applyingFrom', label: 'Applying From' },
      { key: 'citizenship', label: 'Citizenship' },
      { key: 'destination', label: 'Destination' },
      { key: 'country', label: 'Country' },
      { key: 'visaType', label: 'Visa Type' },
      { key: 'processingTime', label: 'Processing Time' },
    ];

    keysToCheck.forEach(({ key, label }) => {
      const val = pkg[key];
      if (val) {
        const parsed = parseValue(val);
        const values = Array.isArray(parsed) ? parsed : [String(parsed)];
        params.push({ label, values });
      }
    });

    // Also check for generic 'parameters' array if specifically populated
    if (pkg.parameters && Array.isArray(pkg.parameters) && params.length === 0) {
      // Logic for generic parameters if needed, or leave as fallback
    }

    return params;
  };

  const fetchInitial = async () => {
    callApi(`${APPLICATION_FORM_SERVICES}?page=1&limit=200`, 'GET').then((res: any) => {
      if (res?.status && res.data?.data?.results) {
        setServices(res.data.data.results);
      }
    });
  };

  const fetchGlobalServices = async () => {
    setLoadingPartnerServices(true);
    try {
      // 1. Fetch global services with package plans
      const globalRes: any = await callApi(
        `${PARTNER_SERVICES}?isGlobal=true&page=1&limit=200`,
        'GET'
      );

      // 2. Fetch already assigned services for this partner to prevent duplicates (only if partnerId exists)
      let assignedRes: any = null;
      if (partnerId) {
        assignedRes = await callApi(
          `${PARTNER_SERVICES}?partnerId=${partnerId}&page=1&limit=200`,
          'GET'
        );
      }

      setLoadingPartnerServices(false);

      if (globalRes?.status && globalRes.data?.data?.results) {
        const globals = globalRes.data.data.results;

        if (assignedRes?.data?.data?.results) {
          const assignedPackageIds = new Set(
            assignedRes.data.data.results.map((item: any) => item.packageId)
          );
          // Filter out globals that are already assigned to this partner
          const filteredGlobals = globals.filter(
            (item: any) => !assignedPackageIds.has(item.packageId)
          );
          setPartnerServices(filteredGlobals);
        } else {
          setPartnerServices(globals);
        }
      }
    } catch (error) {
      setLoadingPartnerServices(false);
      console.error('Failed to fetch services', error);
    }
  };

  const handleServiceChange = (val: any) => {
    form.setFieldsValue({ packageId: undefined });
    setPackages([]);
    if (val) {
      fetchPackages(val);
    }
  };

  const handlePackageChange = (val: any) => {
    const pkg = packages.find(p => p.id === val);
    if (pkg) {
      console.log('Selected Package:', pkg); // Debug log to see package structure
      setSelectedPackage(pkg);
    }
  };

  const handleSelectGlobal = (item: any) => {
    const alreadySelected = selectedGlobalServiceIds.includes(item.id);
    let newIds: string[] = [];

    if (alreadySelected) {
      newIds = selectedGlobalServiceIds.filter(id => id !== item.id);
    } else {
      newIds = [...selectedGlobalServiceIds, item.id];
    }

    setSelectedGlobalServiceIds(newIds);

    // If exactly one item selected, pre-fill form for price editing
    if (newIds.length === 1) {
      const selectedItem =
        item.id === newIds[0] ? item : partnerServices.find(s => s.id === newIds[0]);
      if (selectedItem) {
        const defaultPrice =
          selectedItem.package?.plans?.[0]?.price || selectedItem.customPrice || '';
        form.setFieldsValue({
          name: selectedItem.name,
          packageId: selectedItem.packageId,
          customPrice: defaultPrice,
        });
      }
    } else {
      // Reset price field if 0 or >1 items, to avoid confusion
      form.setFieldsValue({ customPrice: undefined });
    }
  };

  const fetchPackages = (serviceId: string) => {
    setLoadingPackages(true);
    let query = `${APPLICATION_FORM_PACKAGES}/parameters/${serviceId}`;

    callApi(query, 'POST')
      .then((res: any) => {
        setLoadingPackages(false);
        if (res?.status && res.data?.data) {
          setPackages(res.data.data || []);
        } else {
          setPackages([]);
        }
      })
      .catch(() => {
        setLoadingPackages(false);
        setPackages([]);
      });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // --- Multi-Select Submission Logic ---
      if (isAssignMode) {
        const itemsToProcess = partnerServices.filter(s => selectedGlobalServiceIds.includes(s.id));

        if (itemsToProcess.length === 0) {
          toastError('Please select at least one package.');
          return;
        }

        if (onSubmitLocal) {
          // Local submit mode - no price during assignment
          const dataObjects = itemsToProcess.map(item => {
            return {
              packageId: item.packageId,
              package: item.package,
              name: item.name,
              // Price will be set after assignment
            };
          });

          onSubmitLocal(dataObjects);
          onSuccess();
          return;
        }

        // API Submission for Partner Assignment - no price during assignment
        const promises = itemsToProcess.map(item => {
          const payload = {
            partnerId: partnerId,
            packageId: item.packageId,
            name: item.name,
            // Price will be set after assignment
          };

          return callApi(PARTNER_SERVICES, 'POST', payload);
        });

        await Promise.all(promises);
        toastSuccess(
          `${itemsToProcess.length} packages assigned successfully. You can now set prices for each package.`
        );
        onSuccess();
        return;
      }

      // --- Existing Single Edit/Create Logic (Not Assign Mode) ---
      const { serviceId, ...rest } = values;

      const payload = {
        ...rest,
        partnerId: partnerId || values.partnerId || null,
        documitraFee: values.documitraFee ? Number(values.documitraFee) : undefined,
        customPrice:
          values.customPrice !== undefined && values.customPrice !== null
            ? Number(values.customPrice)
            : values.documitraFee
              ? Number(values.documitraFee)
              : undefined,
        id: !initialData && generatedId ? generatedId : undefined,
      };

      const url = initialData ? `${PARTNER_SERVICES}/${initialData.id}` : PARTNER_SERVICES;
      const method = initialData ? 'PATCH' : 'POST';

      const res: any = await callApi(url, method, payload);
      if (res?.status && (res?.data?.isSuccess || res?.data?.statusCode === 200)) {
        toastSuccess(initialData ? 'Package updated successfully' : 'Package saved successfully');
        onSuccess();
      } else {
        const errorMsg = res?.data?.message || res?.data?.data?.message || 'Failed to save package';
        toastError(errorMsg);
      }
    } catch (e: any) {
      console.error('Validation or API error:', e);
      if (e?.errorFields) {
        toastError('Please fill all required fields correctly');
      }
    }
  };

  const isAssignMode = (!!partnerId || isGlobalSelectionMode) && !initialData;
  const isEditMode = !!initialData;

  const isButtonDisabled = isAssignMode
    ? selectedGlobalServiceIds.length === 0
    : !aliasName?.trim() || !serviceId || !packageId || !documitraFee;

  const okText = isEditMode
    ? 'Update Service'
    : isAssignMode
      ? `Assign Selected (${selectedGlobalServiceIds.length})`
      : 'Create Service';

  return (
    <Modal
      title={
        isEditMode
          ? 'Edit Package Service'
          : partnerName
            ? `Assign Package to ${partnerName}`
            : 'Create Package Service'
      }
      visible={visible}
      onCancel={onCancel}
      width={650}
      destroyOnClose
      className="assign-package-modal-rounded"
      centered
      bodyStyle={{ borderRadius: '8px' }}
      footer={
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '10px',
            width: '100%',
          }}
        >
          {isEditMode && onDelete && (
            <Popconfirm
              key="delete-confirm"
              title="Are you sure you want to delete this package service?"
              onConfirm={() => onDelete(initialData.id)}
              okText="Yes"
              cancelText="No"
            >
              <InvertFillButton
                variant="red"
                size="md"
                fontSize="base"
                fontWeight="semibold"
                type="button"
              >
                Delete
              </InvertFillButton>
            </Popconfirm>
          )}
          <InvertFillButton
            key="cancel"
            variant="white"
            size="md"
            fontSize="base"
            fontWeight="semibold"
            onClick={onCancel}
            type="button"
          >
            Cancel
          </InvertFillButton>
          <InvertFillButton
            key="submit"
            variant="blue"
            size="md"
            fontSize="base"
            fontWeight="semibold"
            onClick={handleSubmit}
            type="button"
            style={{
              opacity: isButtonDisabled ? 0.5 : 1,
              pointerEvents: isButtonDisabled ? 'none' : 'auto',
              cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
            }}
          >
            {okText}
          </InvertFillButton>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        {partnerId && (
          <Form.Item name="partnerId" hidden>
            <Input />
          </Form.Item>
        )}
        {isAssignMode && (
          <Form.Item name="name" hidden>
            <Input />
          </Form.Item>
        )}
        {isAssignMode && (
          <Form.Item name="packageId" hidden>
            <Input />
          </Form.Item>
        )}

        {isAssignMode ? (
          <>
            <div
              style={{
                maxHeight: '300px',
                overflowY: 'auto',
                border: '1px solid #f0f0f0',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
              }}
            >
              <Spin spinning={loadingPartnerServices}>
                <List
                  dataSource={partnerServices}
                  renderItem={item => {
                    const isSelected = selectedGlobalServiceIds.includes(item.id);
                    return (
                      <List.Item
                        onClick={() => handleSelectGlobal(item)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
                          padding: '10px 15px',
                          borderRadius: '4px',
                          transition: 'all 0.3s',
                          border: isSelected ? '1px solid #1890ff' : '1px solid transparent',
                        }}
                      >
                        <List.Item.Meta
                          title={<strong>{item.name}</strong>}
                          description={`${item.package?.name} (${item.package?.service?.name})`}
                        />
                      </List.Item>
                    );
                  }}
                  locale={{
                    emptyText:
                      'No global services created yet. Create them in Package Management tab first.',
                  }}
                />
              </Spin>
            </div>

            {selectedGlobalServiceIds.length > 0 && (
              <div
                style={{
                  padding: '10px',
                  background: '#e6f7ff',
                  border: '1px solid #91d5ff',
                  borderRadius: '4px',
                  color: '#0050b3',
                }}
              >
                {selectedGlobalServiceIds.length} package
                {selectedGlobalServiceIds.length > 1 ? 's' : ''} selected. Prices can be set after
                assignment.
              </div>
            )}
          </>
        ) : (
          <>
            {/* Section 1: New Service Name */}
            <div style={{ marginBottom: '24px' }}>
              <h3
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#262626',
                  marginBottom: '2px',
                  paddingBottom: '8px',
                }}
              >
                <span style={{ color: '#ff4d4f', marginRight: '4px' }}>*</span>
                1. New Service Name
              </h3>
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: 'Please give a name' },
                  { whitespace: true, message: 'Name cannot be empty spaces' },
                ]}
              >
                <Input
                  placeholder="Enter custom service name"
                  className="content-rounded-field"
                  style={{ height: '40px', borderRadius: '8px' }}
                />
              </Form.Item>
            </div>

            {/* Section 2: Assign Service Section */}
            <div style={{ marginBottom: '24px' }}>
              <h3
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#262626',
                  marginBottom: '2px',
                  paddingBottom: '8px',
                  paddingTop: '10px',
                  borderTop: '2px solid #a1a1a1ff',
                }}
              >
                2. Assign Service Section
              </h3>

              <Form.Item
                name="serviceId"
                label="Service Name from Main Portal"
                rules={[{ required: true, message: 'Please select a service' }]}
              >
                <Select
                  placeholder="Select Service"
                  onChange={handleServiceChange}
                  showSearch
                  optionFilterProp="children"
                  dropdownClassName="select-checkbox-dropdown"
                  disabled={isEditMode && !!initialData.partnerId}
                  className="content-rounded-field"
                  style={{ height: '40px', borderRadius: '8px' }}
                >
                  {services.map(s => (
                    <Select.Option key={s.id} value={s.id}>
                      {s.name} (ID: {s.id})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="packageId"
                label="Package Name from Main Portal"
                rules={[{ required: true, message: 'Please select a package' }]}
              >
                <Select
                  placeholder="Select Package"
                  disabled={
                    loadingPackages ||
                    (!packages.length && !loadingPackages) ||
                    (isEditMode && !!initialData.partnerId)
                  }
                  loading={loadingPackages}
                  onChange={handlePackageChange}
                  showSearch
                  optionFilterProp="children"
                  dropdownClassName="select-checkbox-dropdown"
                  className="content-rounded-field"
                  style={{ height: '40px', borderRadius: '8px' }}
                  notFoundContent={
                    loadingPackages ? (
                      <Spin size="small" />
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )
                  }
                >
                  {packages.map(p => (
                    <Select.Option key={p.id} value={p.id}>
                      {p.name} (ID: {p.id})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              {selectedPackage && (
                <div
                  style={{
                    padding: '16px',
                    background: '#e6f7ff',
                    borderRadius: '8px',
                    marginTop: '16px',
                    border: '1px solid #91d5ff',
                  }}
                >
                  <div style={{ marginBottom: '12px' }}>
                    <strong style={{ color: '#0050b3' }}>Package ID from Main Portal:</strong>
                    <span style={{ marginLeft: '8px', color: '#262626' }}>
                      {selectedPackage.id || 'N/A'}
                    </span>
                  </div>

                  <div style={{ marginBottom: '0' }}>
                    <strong style={{ color: '#0050b3' }}>
                      Package Parameters from Main Portal:
                    </strong>
                    <div
                      style={{
                        marginTop: '8px',
                        padding: '8px 12px',
                        background: '#fff',
                        borderRadius: '4px',
                        color: '#262626',
                      }}
                    >
                      {(() => {
                        const params = getParameterTags(selectedPackage);

                        if (params.length === 0) {
                          // Fallback for generic logic
                          let genericParams =
                            selectedPackage.parameters ||
                            selectedPackage.params ||
                            selectedPackage.formFields ||
                            selectedPackage.fields ||
                            selectedPackage.packageParameters ||
                            [];

                          if (Array.isArray(genericParams) && genericParams.length > 0) {
                            if (typeof genericParams[0] === 'object') {
                              return genericParams
                                .map(
                                  (p: any) => p.name || p.label || p.fieldName || JSON.stringify(p)
                                )
                                .join(', ');
                            }
                            return genericParams.join(', ');
                          }
                          return 'No parameters found.';
                        }

                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {params.map((param, idx) => (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center' }}>
                                <Tooltip
                                  title={
                                    <div
                                      style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '4px',
                                        maxWidth: '400px',
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                      }}
                                    >
                                      {param.values.map((v, i) => (
                                        <Tag key={i} style={{ margin: 0 }}>
                                          {v}
                                        </Tag>
                                      ))}
                                    </div>
                                  }
                                  color="white"
                                  overlayStyle={{ maxWidth: '450px' }}
                                  overlayInnerStyle={{ color: 'black', maxWidth: 'none' }}
                                >
                                  <span
                                    style={{
                                      fontSize: '13px',
                                      color: '#1890ff',
                                      cursor: 'pointer',
                                      borderBottom: '1px dashed #1890ff',
                                    }}
                                  >
                                    {param.label} ({param.values.length})
                                  </span>
                                </Tooltip>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Section 3: Documitra Fee */}
            <div style={{ marginBottom: '0' }}>
              <h3
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#262626',
                  paddingTop: '10px',
                  marginBottom: '2px',
                  borderTop: '2px solid #a1a1a1ff',
                }}
              >
                3. Documitra Fee
              </h3>
              <Form.Item
                name="documitraFee"
                label="Pricing to be set as default"
                rules={[{ required: true, message: 'Please set Documitra fee' }]}
                tooltip="This is the default pricing that will be used as Documitra's fee"
              >
                <Input
                  type="number"
                  prefix="$"
                  placeholder="Enter Documitra fee"
                  className="content-rounded-field"
                  style={{ height: '40px', borderRadius: '8px' }}
                />
              </Form.Item>
            </div>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default AssignPackageModal;
